import { useQuery } from '@tanstack/react-query';
import useResizeObserver from 'beautiful-react-hooks/useResizeObserver';
import clsx from 'clsx';
import { useState, memo, useRef, useMemo } from 'react';
import type { SwipeCallback } from 'react-swipeable';
import { useSwipeable } from 'react-swipeable';

import Item from './item';

import type { EditorialContent } from '@cobalt/utilities';
import { useWindowSize } from '@hooks/useWindowSize';
import { getWebContainerQuery } from '@queries/editorial';
import { useTranslateFn } from '@shared/Translate';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { competitionCode } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

export const App = ({
  options,
}: {
  options: {
    selectedItem?: number;
    showDisclaimer?: boolean;
    competitionId?: string;
    seasonYear?: string;
    webcontainerName: string;
    showAllTitles: boolean;
    enableCompetitionLink?: boolean;
    theme?: string;
    disableInteraction?: boolean;
    maxVisibleItems?: number;
  };
}) => {
  const t = useTranslateFn();
  const [currentIndex, setCurrentIndex] = useState(options.selectedItem ?? 0);
  const { isMobile, isTablet, isDesktop, isDesktopSmall } = useWindowSize();
  const timelineRef = useRef<HTMLDivElement>(null);
  const DOMRect = useResizeObserver(timelineRef);
  const maxVisibleItems = options.maxVisibleItems ?? 5;

  const isSmallScreen = isMobile || isTablet || isDesktopSmall;
  const disableInteraction = options.disableInteraction && !isSmallScreen;

  const { data: steps } = useQuery(getWebContainerQuery(options.webcontainerName));

  const visibleItems = useMemo(() => {
    if (isSmallScreen) return 3;

    return steps.length > maxVisibleItems ? maxVisibleItems : steps.length;
  }, [isSmallScreen, steps.length, maxVisibleItems]);

  const itemWidth = useMemo(
    () => (DOMRect?.width ? Math.floor(DOMRect.width) / visibleItems : null),
    [DOMRect?.width, visibleItems],
  );

  const trackEvent = (index: number) => {
    GoogleTagManagerHelper.event(
      'Click',
      'TIMELINE',
      `step ${index + 1}`,
      {
        section_reference: 'ticketing',
        interaction: 'TIMELINE',
      },
      'ticketing',
    );
  };

  const handleStepClick = (newIndex) => {
    if (disableInteraction) {
      return;
    }
    trackEvent(newIndex);
    setCurrentIndex(newIndex);
  };

  const handleSwipeLeft: SwipeCallback = () => {
    const canSwipe = itemWidth * currentIndex < itemWidth * (steps.length - 1);
    if (canSwipe) {
      trackEvent(currentIndex + 1);
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleSwipeRight: SwipeCallback = () => {
    if (currentIndex > 0) {
      trackEvent(currentIndex - 1);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const listStyle = useMemo(
    () => ({
      width: `${itemWidth * steps.length}px`,
      transform: (!isDesktop || visibleItems < steps.length) && `translateX(${-1 * (itemWidth * currentIndex)}px)`,
      marginLeft: !isDesktop && `calc(50% - ${itemWidth / 2}px)`,
    }),
    [itemWidth, steps.length, visibleItems, currentIndex, isDesktop],
  );

  const handlers = useSwipeable({
    onSwipedLeft: handleSwipeLeft,
    onSwipedRight: handleSwipeRight,
    preventScrollOnSwipe: false,
  });

  return (
    <div
      ref={timelineRef}
      className={clsx(
        'pk-overflow--hidden',
        styles.timeline,
        options.showAllTitles && isDesktop && styles.showAllTitles,
      )}
      pk-theme={options.theme}
    >
      <div {...handlers} className={clsx(styles.timelineWrapper)}>
        <ul
          className={clsx(styles.timelineList, 'timeline-list', disableInteraction && styles.nonInteractive)}
          style={listStyle}
        >
          {steps?.map((step: EditorialContent, index: number) => (
            <Item
              key={`tl-item-${step.id}`}
              index={index}
              selected={!disableInteraction && index === currentIndex}
              item={step}
              stepClick={handleStepClick}
              enableCompetitionLink={options.enableCompetitionLink}
              itemWidth={itemWidth}
            />
          ))}
        </ul>
      </div>
      {options.showDisclaimer && (
        <div className={clsx(styles.timelineDisclaimer)}>{t(`${competitionCode}_timeline_disclaimer`)}</div>
      )}
    </div>
  );
};

export default memo(App);
